import { UITranscript } from '../types/transcript'
import { computed, ComputedRef, ref } from '@vue/composition-api'
import {
  getWordId,
  getWordPartIndex,
  getWordTimecodes,
  isPunctuationMark,
  isWordElement,
  normalizePunctuation,
  punctuanions
} from '../utils'
import { v4 as uuidv4 } from 'uuid'
import { UISpeaker, Word } from '../models/word'

export default function(
  transcript: UITranscript | undefined | null,
  isSafari = false
) {
  let id = transcript?.id || 0
  const version = ref<number>(transcript?.version || 0)
  const parts = ref<Array<Word>>(transcript ? transcript.parts : [])
  const sentences = ref<Array<Word>>(transcript ? transcript.sentences : [])
  const words = ref<Array<Word>>(transcript ? transcript.words : [])

  /**
   * Applying word indices and spaces to words. Probably deprecated but do not touch yet
   */
  function applyWordIndicesAndSpace(words: Word[]) {
    words.forEach((w, index) => {
      w.index = index
      w.isSpaceRequired =
        words[index + 1] && !isPunctuationMark(words[index + 1].text)
    })

    return words
  }

  /**
   * Increase a version of part or word
   */
  function increaseVersion(version: number | undefined) {
    return version === undefined ? 0 : version + 1
  }

  /**
   * Deprecated?
   */
  function updatePart(part: Word, index: number): Word {
    part.edited = false
    parts.value.splice(index, 1)
    parts.value.push(part)
    parts.value = parts.value.sort((p1, p2) => p1.start - p2.start)
    words.value = []
    parts.value.forEach((part, index) => {
      part.index = index
      if (part.words) {
        words.value.push(...part.words)
      }
    })
    words.value.forEach((w, index) => (w.index = index))

    return part
  }

  /**
   * Initializing transcript value
   */
  function initTranscript(transcript: UITranscript) {
    id = transcript.id
    version.value = transcript.version
    parts.value = JSON.parse(JSON.stringify(transcript.parts))
    sentences.value = JSON.parse(JSON.stringify(transcript.sentences))
    words.value = JSON.parse(
      JSON.stringify(applyWordIndicesAndSpace(transcript.words))
    )
  }

  const value: ComputedRef<UITranscript> = computed(() => ({
    id,
    version: version.value,
    parts: parts.value,
    sentences: sentences.value,
    words: words.value
  }))

  /**
   * Maps DOM elements array to the array of Word[] elements
   * @param words - dom elements
   * @param speaker - speaker to assign to each word
   */
  function mapDomWords(words: HTMLElement[], speaker: UISpeaker = {}) {
    return words.map((word: HTMLElement) => ({
      text: word.innerText.trim(),
      isPunctuation: word.dataset.punctuation! === 'true',
      start: parseInt(word.dataset.timecodeStart!),
      end: parseInt(word.dataset.timecodeEnd!),
      index: parseInt(word.dataset.index!),
      partIndex: parseInt(word.dataset.partIndex!),
      id: word.dataset.id,
      isSpare: !word.innerText.trim().length,
      edited: word.className.includes('single'),
      version: parseInt(word.dataset.version!) || 0,
      speaker
    }))
  }

  /**
   * Updates transcript
   */
  function updTranscript(transcript: UITranscript) {
    id = transcript.id
    parts.value = JSON.parse(JSON.stringify(transcript.parts))
    parts.value.forEach(p => {
      p.version = p.version === undefined ? 0 : p.version + 1
    })
    sentences.value = JSON.parse(JSON.stringify(transcript.sentences))
    words.value = JSON.parse(JSON.stringify(transcript.words))
  }

  /**
   * Extracting words from a string
   */
  function extractWords(_text: string): string[] {
    if (!_text || !_text.trim().length) {
      return []
    }

    const text = normalizePunctuation(_text.trim())

    if (isPunctuationMark(text)) {
      return [text]
    }

    const words: string[] = text.split(' ')

    const punctuationIndex = text
      .split('')
      .findIndex(c => punctuanions.includes(c))
    if (punctuationIndex === -1) {
      return words
    }

    const newWords: string[] = []
    words.forEach(w => {
      const words: string[] = []
      let punctuationIndex = w.split('').findIndex(w => isPunctuationMark(w))

      if (punctuationIndex === -1) {
        newWords.push(w)
        return
      }

      while (punctuationIndex > -1 && w.length) {
        if (punctuationIndex === 0) {
          words.push(w[0])
          w = w.slice(1, w.length)
          punctuationIndex = w
            .split('')
            .findIndex(c => punctuanions.includes(c))
          continue
        }

        words.push(w.slice(0, punctuationIndex))
        words.push(w[punctuationIndex])
        w = w.slice(punctuationIndex + 1, w.length)

        punctuationIndex = w.split('').findIndex(c => punctuanions.includes(c))
      }
      words.push(w)

      newWords.push(...words.filter(w => w))
    })

    return newWords
  }

  /**
   * Looks for spare words before a word
   * @param index - word index to look before
   * @param arr - array of words to look in
   * @returns array of spare words
   */
  function getSpareWordsBefore(index: number | undefined, arr: Word[]): Word[] {
    const spareWords: Word[] = []
    if (!index || index === -1) {
      return spareWords
    }

    for (let i = index - 1; i >= 0; i--) {
      if (arr[i] && arr[i].isSpare) {
        spareWords.push(arr[i])
        if (!arr[i - 1] || !arr[i - 1].isSpare) {
          break
        }
      } else {
        break
      }
    }

    return spareWords
  }

  /**
   * Looks for spare words after a word
   * @param index - word index to look after
   * @param arr - array of words to look in
   * @returns array of spare words
   */
  function getSpareWordsAhead(index: number | undefined, arr: Word[]): Word[] {
    const spareWords: Word[] = []
    if (index === undefined || index === -1) {
      return spareWords
    }

    for (let i = index + 1; i < arr.length; i++) {
      if (arr[i].isSpare) {
        spareWords.push(arr[i])
        if (!arr[i + 1] || !arr[i + 1].isSpare) {
          break
        }
      } else {
        break
      }
    }

    return spareWords
  }

  /**
   * Looks for non-spare word after a word
   * @param index - word index to look after
   * @param arr - array of words to look in
   * @returns closest non-spare word
   */
  function getClosestNonSpareWord(
    index: number,
    arr: Word[]
  ): Word | undefined {
    if (!arr[index + 1] || arr[index + 1].isSpare) {
      return
    }

    return arr.find((w, i) => i > index && !w.isSpare)
  }

  /**
   * Looks for non-spare word before a word
   * @param index - word index to look before
   * @param arr - array of words to look in
   * @returns closest non-spare word
   */
  function getClosestNonSpareWordBefore(
    index: number,
    arr: Word[]
  ): Word | undefined {
    if (!arr[index - 1] || (arr.length === 0 && arr[0].isSpare)) {
      return
    }

    const nonSpareWords = arr.filter((w, i) => i < index && !w.isSpare)

    return nonSpareWords[nonSpareWords.length - 1]
  }

  /**
   * Restores words that are not present in DOM because they are spare
   */
  function restoreDeletedWords(
    domWords: Word[],
    originalSlice: Word[]
  ): Word[] {
    let words: Word[] = [...domWords]
    let partId: string | undefined

    originalSlice.forEach((word: Word) => {
      const w = domWords.find(_w => _w.id === word.id)
      partId = word.partId

      if (!w) {
        words.push({
          start: word.start,
          end: word.end,
          text: '',
          isSpare: !word.isPunctuation,
          isPunctuation: !!word.isPunctuation,
          partIndex: word.partIndex,
          id: word.id,
          edited: word.edited,
          speaker: word.speaker,
          index: word.index
        })
      }
    })

    words.forEach(w => {
      w.partId = partId
    })
    words = words.sort((a, b) => a.start - b.start)
    return words
  }

  /**
   * Extracts single word into Words[] array
   */
  function resolveSingleWord(
    texts: string[],
    index: number,
    word: Word,
    prevWord: Word | null,
    allowPunctuation = true
  ): [Word[], string[]] {
    const result: Word[] = []
    const wordsTexts = [...texts]

    if (!word) {
      return [result, wordsTexts]
    }

    if (!prevWord) {
      prevWord = words.value[word.index - 1]
    }

    const spareWord: Word = { ...word, text: '', isSpare: true, id: uuidv4() }

    const text = wordsTexts[index]
    const nextText = wordsTexts[index + 1]

    wordsTexts.splice(0, index + 1)

    if (!text) {
      return [[spareWord], wordsTexts]
    }

    if (isPunctuationMark(text)) {
      // edit the punctuation
      if (word.isPunctuation) {
        result.push({
          ...word,
          text,
          isSpare: false
        })

        return [result, wordsTexts]
      }

      // Do not allow to start transcript with punctuation
      if (!prevWord) {
        if (!nextText) {
          result.push(spareWord)
          return [result, wordsTexts]
        }

        result.push({ ...word, text: nextText, isSpare: false })
        wordsTexts.shift()

        return [result, wordsTexts]
      }

      // do not allow to have more than 1 punctuation mark in a row
      // make word spare if it's a punctuation and there's a punctuation before

      if (prevWord.isPunctuation) {
        result.push({
          ...prevWord,
          text
        })
        result.push(spareWord)

        return [result, wordsTexts]
      }

      // punctuation inherits the timecode of the previous word
      result.push({
        ...prevWord,
        text: text,
        id: uuidv4(),
        index: -1,
        isPunctuation: true,
        start: prevWord.end,
        end: prevWord.end
      })

      // timecode becomes spare
      if (allowPunctuation) {
        wordsTexts.shift()

        if (!nextText) {
          result.push(spareWord)
          return [result, wordsTexts]
        }

        result.push({ ...word, text: nextText, isSpare: false })

        return [result, wordsTexts]
      } else {
        result.push(spareWord)

        return [result, wordsTexts]
      }
    } else {
      result.push({
        ...word,
        text,
        isSpare: false
      })

      return [result, wordsTexts]
    }
  }

  /**
   * Fills spare words
   * @param spareWords - array of spare words
   * @param wordTexts - array of words to fill
   * @param after - dont remember, do not touch :)
   */
  function fillSpareWords(
    spareWords: Word[],
    wordTexts: string[],
    after: Word
  ): [Word[], string[], Word[]] {
    const filledWords: Word[] = spareWords.map(w => ({ ...w }))
    const usedSpareWords: Word[] = []

    if (!spareWords.length) {
      return [filledWords, wordTexts, usedSpareWords]
    }

    const _words = [...wordTexts]
    let restWords = [...wordTexts]

    let index = 0
    while (index < filledWords.length) {
      const text = _words[index]
      let t: Word[] = []
      let filled = false

      if (text === undefined) {
        restWords.shift()
        index++
        continue
      }

      do {
        const [w, texts] = resolveSingleWord(
          _words,
          index,
          filledWords[index],
          filledWords[index - 1] || after
        )

        restWords = [...texts]
        t = w
        filled = !t.find(w => w.isSpare)

        if (!filled) {
          _words.shift()
        }
      } while (!filled && _words.length && restWords.length)

      if (!filled) {
        // for punctuation added only
        if (t.length > 1 && t[0].isPunctuation && t[1].isSpare) {
          filledWords[index] = t[1]
          filledWords.unshift(t[0])
          _words.shift()
        }

        continue
      }

      switch (t.length) {
        case 1: {
          filledWords[index] = t[0]
          usedSpareWords.push(filledWords[index])

          break
        }
        case 2: {
          filledWords[index] = t[1]
          usedSpareWords.push(filledWords[index])
          filledWords.unshift(t[0])
          break
        }
        default: {
          break
        }
      }

      index += t.length
    }

    filledWords.forEach(w => (w.isSpare = false))
    return [filledWords, restWords, usedSpareWords]
  }

  /**
   * Edits the timecode of the first word in part
   * @param words - words to insert
   * @param word - first word
   */
  function editFirstWord(words: string[], word: Word) {
    const newFirstWordStart = word.end - (word.end - word.start) / 2
    const duration =
      (newFirstWordStart - 1 - word.start) /
      words.filter(w => !isPunctuationMark(w)).length

    let startFrom = word.start

    const firstWord = words.pop()

    if (!firstWord) {
      return null
    }

    const newWords = words.map((w, i, arr) => {
      let endAt
      const isPunctuation = isPunctuationMark(w)

      if (isPunctuation) {
        startFrom = startFrom >= word.end ? word.end : startFrom
        endAt = startFrom
      } else {
        startFrom = startFrom >= word.end ? word.end : startFrom
        endAt =
          startFrom + duration >= word.end ? word.end : startFrom + duration
      }

      const res: Word = {
        ...word,
        text: w,
        isSpare: false,
        isPunctuation,
        id: uuidv4(),
        start: Math.round(startFrom),
        end: Math.round(endAt)
      }

      if (isPunctuation) {
        startFrom += 1
      } else {
        startFrom = startFrom + duration
      }

      return res
    })

    newWords.push({
      ...word,
      text: firstWord,
      start: newFirstWordStart
    })

    return newWords
  }

  /**
   * Inserts words between timecodes
   * @param words - words too insert
   * @param start - timecode to insert after
   * @param end - timecode to insert before
   * @param extend - word to inherit properties from
   * @returns array of inserted words
   */
  function insertWordsBetweenTimecodes(
    words: string[],
    start: number,
    end: number,
    extend: Word
  ): Word[] {
    let res: Word[] = []

    let startFrom = start
    const endAt = end

    const duration =
      (endAt - startFrom) / words.filter(w => !isPunctuationMark(w)).length || 1

    // each new word before spare one has an equal duration
    res = words.map((w, i, arr) => {
      let endAt
      const isPunctuation = isPunctuationMark(w)

      if (isPunctuation) {
        startFrom = startFrom >= end ? end : startFrom
        endAt = startFrom
      } else {
        startFrom = startFrom >= end ? end : startFrom
        endAt = startFrom + duration >= end ? end : startFrom + duration
      }

      const res: Word = {
        ...extend,
        id: uuidv4(),
        insertedBetween: true,
        isPunctuation,
        text: w,
        isNew: true,
        start: Math.round(startFrom),
        end: Math.round(endAt),
        isSpare: false
      }

      if (isPunctuation) {
        startFrom += 1
      } else {
        startFrom = startFrom + duration
      }

      return res
    })

    return res
  }

  /**
   * Inserts words between timecodes
   * @param words - words too insert
   * @param after - word to insert after
   * @param before - word to insert before
   */
  function insertWordsBetween(
    words: string[],
    after: Word,
    before: Word
  ): [Word[], Word, Word] {
    let res: Word[] = []

    if (!after || !before) {
      return [res, after, before]
    }

    // Shift timecodes a bit if no space left

    if (!before.isPunctuation || !isPunctuationMark(before.text[0] || '')) {
      if (before.start === after.end) {
        before.start += 2
      } else if (before.start === after.end + 1) {
        before.start += 1
      }

      if (before.start > before.end) {
        before.end = before.start
      }
    } else {
      // Find spare words after punctuation
    }

    let startFrom = after.end
    const endAt = before.start

    const duration =
      (endAt - startFrom) / words.filter(w => !isPunctuationMark(w)).length

    // each new word before spare one has an equal duration
    res = words.map((w, i, arr) => {
      let endAt
      const isPunctuation = isPunctuationMark(w)

      if (isPunctuation) {
        startFrom = startFrom >= before.start ? before.start : startFrom
        endAt = startFrom
      } else {
        startFrom = startFrom >= before.start ? before.start : startFrom
        endAt =
          startFrom + duration >= before.start
            ? before.start
            : startFrom + duration
      }

      const res: Word = {
        ...before,
        id: uuidv4(),
        insertedBetween: true,
        isPunctuation,
        partIndex: (after.partIndex || 0) + i,
        text: w,
        isNew: true,
        start: Math.round(startFrom),
        end: Math.round(endAt),
        isSpare: false
      }

      if (isPunctuation) {
        startFrom += 1
      } else {
        startFrom = startFrom + duration
      }

      return res
    })

    return [res, after, before]
  }

  /**
   * Inserts words after words
   * @param words - words too insert
   * @param after - word to insert after
   * @param duration - word duration
   * @param startFrom - timecode to start from
   * @param endAt - timecode to and at
   * @returns array of inserted words
   */
  function insertWordsAfter(
    words: string[],
    after: Word,
    duration: number,
    startFrom: number,
    endAt: number
  ): Word[] {
    return words.map((w, i) => {
      const res: Word = {
        ...after,
        text: w,
        start: Math.round(startFrom),
        insertedBetween: true,
        isSpare: !w.length,
        isNew: true,
        end: Math.round(
          startFrom + duration >= endAt ? endAt - 1 : startFrom + duration
        )
      }

      startFrom = startFrom + duration + 1

      return res
    })
  }

  /**
   * Inserts words at the end of part
   * @param w - words to insert
   * @param lastWord - last word of the part
   * @returns array of inserted words
   */
  function insertWordsAtTheEndOfPart(w: string[], lastWord: Word): Word[] {
    if (!w.length) {
      return []
    }

    const part = parts.value.find(
      p => p.end >= lastWord.end && p.start <= lastWord.start
    )
    if (!part) {
      return []
    }

    if (part.end > lastWord.end + 1) {
      const timeRemaining = part.end - lastWord.end
      const wordDuration = timeRemaining / w.length
      const startFrom = lastWord.end + 1
      return insertWordsAfter(w, lastWord, wordDuration, startFrom, part.end)
    } else {
      return w.map((word, i) => ({
        ...lastWord,
        text: word,
        start: lastWord.end,
        end: lastWord.end,
        id: uuidv4(),
        isPunctuation: isPunctuationMark(word),
        insertedBetween: true,
        isSpare: false,
        isNew: true,
        isSpaceRequired: w[i + 1] ? !isPunctuationMark(w[i + 1]) : true
      }))
    }
  }

  /**
   * Inserts words after word in array of words
   * @param words - words to insert
   * @param word - word to insert after
   * @param wordsArr - array of words
   * @returns array of words to insert
   */
  function insertWordsAfterWord(
    words: string[],
    word: Word,
    wordsArr: Word[]
  ): [Word[], Word?] {
    if (!word) {
      return [[]]
    }
    const closestNonSpareWord = getClosestNonSpareWord(
      wordsArr.findIndex(w => w.id === word.id),
      wordsArr
    )
    let restWords: Word[] = []

    // a) split the available time between words
    if (closestNonSpareWord) {
      const [_restWords, after, before] = insertWordsBetween(
        words,
        word,
        closestNonSpareWord
      )
      restWords = _restWords

      const idx = wordsArr.findIndex(w => w.id === before.id)
      for (let i = idx; i < wordsArr.length; i++) {
        const w = wordsArr[i]
        w.partIndex = (w.partIndex || 0) + restWords.length
      }

      return [restWords, before]
    } else {
      // a) insert words at the end of part
      restWords = insertWordsAtTheEndOfPart(
        words,
        wordsArr[wordsArr.length - 1]
      )
    }

    return [restWords]
  }

  /**
   * Extracts multiple words into Words[] array
   * @param __words - words to insert
   * @param _word - corresponding word of previous version
   * @param wordsArr - array of words of previous version
   * @param wordsUntilCurrent - array of words filled till the corresponding word
   */
  function resolveMultipleWords(
    __words: string[],
    _word: Word,
    wordsArr: Word[],
    wordsUntilCurrent: Word[]
  ): [Word[], Word[]] {
    const result: Word[] = []
    const word: Word | undefined = wordsArr.find(w => w.id === _word.id)
    let usedSpareWords: Word[] = []

    if (!word) {
      return [result, usedSpareWords]
    }

    // edit the first word
    const [firstWord, _words] = resolveSingleWord(
      __words,
      0,
      word,
      wordsUntilCurrent[wordsUntilCurrent.length - 1],
      true
    )

    firstWord.forEach(w => (w.edited = true))

    const spareWordsAhead = getSpareWordsAhead(word.partIndex, wordsArr)

    //

    // If there's no spare timecodes after the word in a specific range, we need to:
    if (!spareWordsAhead.length) {
      const [restWords, changedWord] = insertWordsAfterWord(
        _words,
        word,
        wordsArr
      )

      result.push(...firstWord, ...restWords)

      return [result, usedSpareWords]
    }

    // Fill spare timecodes
    const [filledWords, remainingWords, _usedSpareWords] = fillSpareWords(
      spareWordsAhead,
      _words,
      word
    )

    usedSpareWords = _usedSpareWords

    // If there were no spare timecode for some words, we need to split the available time
    if (remainingWords.length) {
      const [restWords, changedWord] = insertWordsAfterWord(
        remainingWords,
        filledWords[filledWords.length - 1],
        wordsArr
      )

      result.push(...firstWord, ...filledWords, ...restWords)

      return [result, usedSpareWords]
    } else {
      result.push(...firstWord, ...filledWords)

      return [result, usedSpareWords]
    }
  }

  /**
   * Compares a slice of dom words with the original slice and distributes timecodes
   * @param wordEls - dom words
   * @param originalSlice - originalSlice of corresponding words
   */
  function format(wordEls: HTMLElement[], originalSlice: Word[]): Word[] {
    const domWords: Word[] = mapDomWords(wordEls)

    const result: Word[] = []

    const _words = restoreDeletedWords(domWords, originalSlice)

    let index = 0
    let atLeastOneWordFilled = false

    //

    while (index < _words.length) {
      const word = _words[index]

      const correspondingWord = originalSlice.find(w => w.id === word.id)
      if (word.text === correspondingWord?.text) {
        result.push(word)
        if (!correspondingWord.isSpare) {
          atLeastOneWordFilled = true
        }

        index++

        continue
      }
      // 'one, two' = ['one', ',', 'two']
      const wordWords = extractWords(word.text)

      switch (wordWords.length) {
        case 0: {
          const spareWord = { ...word, text: '', isSpare: true }
          let exists = result.find(w => w.id === word.id)
          if (exists) {
            exists = spareWord
          } else {
            result.push(spareWord)
          }

          break
        }
        case 1: {
          const [words] = resolveSingleWord(
            wordWords,
            0,
            word,
            result[result.length - 1]
          )

          result.push(...words)

          if (words.length > 1 || !words[0].isSpare) {
            atLeastOneWordFilled = true
          }

          break
        }
        default: {
          const res: Word[] = []
          const spareTimecodesAfter = getSpareWordsAhead(word.partIndex, _words)

          if (index === 0) {
            if (_words.length === 1) {
              const newWords = insertWordsBetweenTimecodes(
                wordWords,
                word.start,
                word.end,
                word
              )
              if (newWords) {
                result.push(...newWords)
              } else {
                result.push(word)
              }

              index++
              continue
            }
            if (spareTimecodesAfter.length) {
              // edit the first word
              const [firstWord, __words] = resolveSingleWord(
                wordWords,
                0,
                word,
                result[result.length - 1],
                false
              )

              // fill spare timecodes ahead
              const [filledWords, remainingWords] = fillSpareWords(
                spareTimecodesAfter,
                __words,
                word
              )

              if (remainingWords.length) {
                const [restWords, changedWord] = insertWordsAfterWord(
                  remainingWords,
                  filledWords[filledWords.length - 1],
                  _words
                )

                res.push(...firstWord, ...filledWords, ...restWords)
                result.push(...res)

                atLeastOneWordFilled = true
                index++

                continue
              } else {
                res.push(...firstWord, ...filledWords)
                result.push(...res)
                index++
                atLeastOneWordFilled = true

                continue
              }
            } else {
              // adjust first word timecode
              const w: Word[] | null = editFirstWord(wordWords, word)

              if (!w) {
                continue
              }

              result.push(...w)

              index += 1
              atLeastOneWordFilled = true
              continue
            }
          }

          const [words, usedSpareWords] = resolveMultipleWords(
            wordWords,
            word,
            _words,
            result
          )

          result.push(...words)

          index = usedSpareWords.length
            ? index + usedSpareWords.length + 1
            : index + 1

          continue
        }
      }

      index++
    }

    const sorted = result
      .sort((w1, w2) => (w1.partIndex || 0) - (w2.partIndex || 0))
      .sort((w1, w2) => w1.start - w2.start)

    const res: Word[] = []
    sorted.forEach((word: Word) => {
      const alreadyIn: Word | undefined = res.find(
        w =>
          w.start === word.start &&
          w.end === word.end &&
          w.partIndex === word.partIndex
      )
      if (alreadyIn && alreadyIn.isSpare && !word.isSpare) {
        alreadyIn.text = word.text
      } else if (!word.isSpare && word.insertedBetween) {
        res.push(word)
      } else if (!alreadyIn) {
        res.push(word)
      }
    })

    res.forEach((w, index) => {
      w.isSpaceRequired =
        w.isSpaceRequired || res[index + 1]
          ? !isPunctuationMark(res[index + 1]?.text)
          : true
      w.partIndex = index

      /*
             For MYAN:
             This one triggers rerender of all words of part because it changes the reference to array.
             Update only changed words instead
             */
      w.version = increaseVersion(w.version)
    })

    return res
  }

  /**
   * Formats part. Might be deprecated
   */
  function formatPart(part: Word, domWords: HTMLElement[]): Word[] {
    if (!part.words || !part.words.length) {
      return []
    }
    part.edited = false
    return format(domWords, part.words)
  }

  /**
   * Formats edited parts
   */
  function formatEdited(editedParts: Word[], htmlWords: HTMLElement[][]) {
    editedParts.forEach((part, index) => {
      if (part.words && parts.value.find(p => p.id === part.id)) {
        const _part = parts.value[part.index]
        _part.words = format(htmlWords[index], part.words)
        _part.edited = false
        _part.version = increaseVersion(_part.version)
      }
    })
  }

  /**
   * Formats all parts
   * @param domParts - array of parts' words' HTML Elements
   * @param copy - should make a copy of the transcript or mutate the existing one
   */
  function formatAll(domParts: HTMLElement[][], copy = false) {
    const _parts: Word[] = []
    domParts.forEach((part, index) => {
      const _part = copy
        ? JSON.parse(JSON.stringify(parts.value[index] as Word))
        : parts.value[index]

      if (!_part || !_part.words) {
        return
      }

      _part.words = formatPart(_part, part)
      _parts.push(_part)
    })

    if (!copy) {
      parts.value = _parts
      words.value = []
      parts.value.forEach((part, index) => {
        part.index = index
        // part.edited = false
        part.version = increaseVersion(part.version)
        if (part.words) {
          words.value.push(...part.words)
          part.words.forEach(w => {
            w.speaker = part.speaker
            w.partId = part.id
          })
        }
      })
      words.value.forEach((w, index) => {
        w.index = index
        w.version = increaseVersion(w.version)
      })

      return {
        ...value.value,
        version: increaseVersion(value.value.version)
      }
    } else {
      const _words: Word[] = []
      _parts.forEach((part, index) => {
        part.index = index
        part.edited = false
        if (part.words) {
          _words.push(...part.words)
          part.words.forEach(w => {
            w.speaker = part.speaker
            w.partId = part.id
          })
        }
      })

      return {
        ...value.value,
        version: increaseVersion(value.value.version),
        parts: _parts,
        words: _words
      }
    }
  }

  /**
   * Inserts empty part before a specific one
   * @param insertBefore - part index to insert before
   * @param currentPart - part html words to insert before
   * @param speaker - speaker to attach to a new part
   */
  function insertEmptyPartBefore(
    insertBefore: number,
    currentPart: HTMLElement[],
    speaker: UISpeaker = {}
  ): Word | null {
    const prevPart = parts.value[insertBefore - 1]
    const part = parts.value[insertBefore]
    const nextPart = parts.value[insertBefore + 1]

    if (!nextPart) {
      return null
    }

    const partWords = part.words

    if (!partWords || !partWords.length || !prevPart) {
      return null
    }

    /*
         For MYAN:
         This one triggers rerender of all words of part because it changes the reference to array.
         Update only changed words instead
         */
    part.words = format(currentPart, partWords)
    part.version = increaseVersion(part.version)

    const isEmptyPart =
      part.words.length === 1 && part.words[0].text.trim().length === 0

    if (isEmptyPart) {
      return null
    }

    const newPartStart = prevPart.end + 1
    const newPartEnd = part.start - 1

    if (newPartStart >= newPartEnd) {
      return null
    }

    part.start = newPartEnd + 1

    const newPart = {
      text: String.fromCharCode(160),
      speaker,
      start: newPartStart,
      end: newPartEnd,
      index: 0,
      isNew: true,
      version: 0,
      words: [
        {
          text: String.fromCharCode(160),
          id: uuidv4(),
          speaker,
          isNew: true,
          start: newPartStart,
          end: newPartEnd,
          isSingleInPart: true,
          index: +new Date(),
          partIndex: 0
        }
      ]
    }

    parts.value.splice(insertBefore, 0, newPart)
    parts.value.forEach((part, index) => {
      part.index = index
    })

    return newPart
  }

  /**
   * Probably deprecated
   */
  function saveWords(index: number, htmlWords: HTMLElement[]) {
    const part = parts.value[index]

    if (!part || !part.words) {
      return null
    }

    part.words = format(htmlWords, part.words)
    part.edited = false
    part.words.forEach((w, index) => {
      w.partIndex = index
    })

    if (part.words.length === 1) {
      part.words[0].text = String.fromCharCode(160)
    }
    // part.isNew = false

    return updatePart(part, index)
  }

  /**
   * Inserts empty part after a specific one
   * @param insertAfter - part index to insert after
   * @param currentPart - part html words to insert after
   * @param speaker - speaker to attach to a new part
   */
  function insertEmptyPart(
    insertAfter: number,
    currentPart: HTMLElement[],
    speaker: UISpeaker = {}
  ): Word | null {
    const prevPart = parts.value[insertAfter]
    const nextPart = parts.value[insertAfter + 1]

    if (!nextPart) {
      return null
    }

    const partWords = prevPart.words

    if (!partWords || !partWords.length || !prevPart.words) {
      return null
    }

    const formattedWords = format(currentPart, partWords)

    /*
         For MYAN:
         This one triggers rerender of all words of part because it changes the reference to array.
         Update only changed words instead
         See commented code below as a sandbox
         */
    prevPart.words = formattedWords

    // Update only words which were changed
    // formattedWords.forEach((formattedWord: Word, index: number) => {
    //     if (!prevPart.words) {
    //         return
    //     }
    //
    //     const correspondingWord = prevPart.words[index]
    //
    //     if (!correspondingWord) {
    //         prevPart.words.push(formattedWord)
    //         return
    //     }
    //
    //     if (formattedWord.isNew) {
    //         prevPart.words.splice(formattedWord.partIndex || 0, 0, formattedWord)
    //         return
    //     }
    //
    //     const wordChanged =
    //         correspondingWord.text !== formattedWord.text ||
    //         correspondingWord.start !== formattedWord.start ||
    //         correspondingWord.end !== formattedWord.end ||
    //         correspondingWord.isSpare !== formattedWord.isSpare ||
    //         correspondingWord.isSpaceRequired !== formattedWord.isSpaceRequired
    //
    //     if (wordChanged) {
    //         correspondingWord.text = formattedWord.text
    //         correspondingWord.start = formattedWord.start
    //         correspondingWord.end = formattedWord.end
    //         correspondingWord.isSpare = formattedWord.isSpare
    //         correspondingWord.isSpaceRequired = formattedWord.isSpaceRequired
    //         correspondingWord.version = increaseVersion(correspondingWord.version)
    //     } else if (formattedWord.edited) {
    //         correspondingWord.version = increaseVersion(correspondingWord.version)
    //     }
    // })

    prevPart.version = increaseVersion(prevPart.version)

    const isEmptyPart =
      prevPart.words.length === 1 && prevPart.words[0].text.trim().length === 0

    if (isEmptyPart) {
      return null
    }

    const newPartStart = prevPart.end + 1

    if (prevPart.end === nextPart?.start) {
      if (nextPart.words && nextPart.words[0]) {
        nextPart.start += 2
        nextPart.words[0].start += 2
      }
    } else if (prevPart.end === nextPart?.start - 1) {
      if (nextPart.words && nextPart.words[0]) {
        nextPart.start += 1
        nextPart.words[0].start += 1
      }
    }

    const newPartEnd = nextPart.start - 1

    const newPart = {
      id: uuidv4(),
      text: String.fromCharCode(160),
      speaker,
      start: newPartStart,
      end: newPartEnd,
      index: 0,
      isNew: true,
      version: 0,
      words: [
        {
          text: String.fromCharCode(160),
          id: uuidv4(),
          isSpare: true,
          speaker,
          isNew: true,
          start: newPartStart,
          end: newPartEnd,
          index: +new Date(),
          isSingleInPart: true,
          partIndex: 0
        }
      ]
    }

    parts.value.splice(insertAfter + 1, 0, newPart)
    parts.value.forEach((part, index) => {
      part.index = index
    })

    return newPart
  }

  /**
   * Splits a specific part
   * @param partIndex - part index to split
   * @param domWords - part words to split
   * @param word - word where cursor is
   * @param offset - cursor offset
   * @param speaker - speaker to apply to a new part
   * @param isEditedWord - ...
   * @param closestNextWord - closest next word, obviously :)
   */
  function splitPart(
    partIndex: number,
    domWords: HTMLElement[],
    word: HTMLElement,
    offset: number,
    speaker: UISpeaker = {},
    isEditedWord = false,
    closestNextWord: HTMLElement | null
  ) {
    const partToSplit: Word = parts.value[partIndex]

    if (!partToSplit || !partToSplit.words) {
      return null
    }

    const focusId = getWordId(word)
    const closestNextWordId = closestNextWord ? getWordId(closestNextWord) : ''

    if (!focusId) {
      return null
    }

    /*
         For MYAN:
         This one triggers rerender of all words of part because it changes the reference to array.
         Update only changed words instead
         */
    partToSplit.words = format(domWords, partToSplit.words)
    partToSplit.version = increaseVersion(partToSplit.version)

    const wordTimecodes = getWordTimecodes(word)
    const wordPartIndex = getWordPartIndex(word)
    const lastWord = partToSplit.words[partToSplit.words.length - 1]
    let splitWord: Word | undefined

    if (isEditedWord && closestNextWordId) {
      splitWord = partToSplit.words.find(w => w.id === closestNextWordId)
      if (splitWord) {
        splitWord = partToSplit.words[(splitWord.partIndex || 0) - 1]
      }
    } else {
      splitWord = partToSplit.words.find(w => w.id === focusId)
    }

    if (!splitWord) {
      return
    }

    if (
      !lastWord ||
      wordTimecodes.start === -1 ||
      wordTimecodes.end === -1 ||
      wordPartIndex === -1
    ) {
      return null
    }

    const newPart: Word = {
      text: 'text',
      id: uuidv4(),
      speaker,
      start: wordTimecodes.start + 1,
      end: partToSplit.end,
      index: partIndex + 1,
      words: [],
      version: increaseVersion(partToSplit.version),
      partIndex: 0
    }

    let firstSlice: Word[] = []
    let secondSlice: Word[] = []

    if (partToSplit.words.length) {
      firstSlice = partToSplit.words.slice(
        0,
        splitWord.partIndex !== undefined ? splitWord.partIndex + 1 : 0
      )
      secondSlice = partToSplit.words.slice(
        splitWord.partIndex !== undefined ? splitWord.partIndex + 1 : 0,
        partToSplit.words.length
      )
    }

    if (!firstSlice.length || !secondSlice.length) {
      return
    }

    if (offset > 0) {
      const firstWordText = splitWord.text.slice(0, offset)
      const secondWordText = splitWord.text.slice(offset, splitWord.text.length)

      firstSlice[firstSlice.length - 1].text = firstWordText

      if (secondWordText) {
        const spareTimecodesAhead: Word[] = []
        const index = 0

        for (let i = index; i < secondSlice.length; i++) {
          if (secondSlice[i].isSpare) {
            spareTimecodesAhead.push(secondSlice[i])
            if (!secondSlice[i + 1] || !secondSlice[i + 1].isSpare) {
              break
            }
          }
        }

        if (spareTimecodesAhead.length) {
          spareTimecodesAhead[0].text = secondWordText
          spareTimecodesAhead[0].isSpare = false

          const idx = secondSlice.findIndex(
            w => w.id === spareTimecodesAhead[0].id
          )
          if (idx === -1) {
            return
          }

          if (secondSlice[idx - 1] && secondSlice[idx - 1].isPunctuation) {
            const punct = secondSlice[idx - 1]
            const wrd = secondSlice[idx]

            punct.start = wrd.end
            punct.end = wrd.end

            punct.partIndex =
              punct.partIndex !== undefined ? punct.partIndex + 1 : 0
            wrd.partIndex = wrd.partIndex !== undefined ? wrd.partIndex - 1 : 0

            secondSlice[idx - 1] = wrd
            secondSlice[idx] = punct
          }

          // secondSlice[0] = spareTimecodesAhead[0]
          // secondSlice[0].isSpare = false
          secondSlice[0].isSpaceRequired = !(
            secondSlice[1] && secondSlice[1].isPunctuation
          )

          if (secondSlice[0].id === secondSlice[1]?.id) {
            secondSlice.splice(0, 1)
          }
        } else {
          const isPunctuation = isPunctuationMark(secondWordText)
          const nextWord = secondSlice[0]
          const prevWord = firstSlice[firstSlice.length - 1]

          const nextWordLength = nextWord.end - nextWord.start

          const newWord: Word = {
            text: secondWordText,
            start: isPunctuation ? prevWord.end : nextWord.start,
            end: isPunctuation
              ? prevWord.end
              : nextWord.end - nextWordLength / 2,
            speaker: {},
            id: uuidv4(),
            index: 0,
            partIndex: 0,
            isPunctuation,
            isSpaceRequired: true
          }

          if (!isPunctuation) {
            nextWord.start = nextWord.end - nextWordLength / 2
          }

          secondSlice.unshift(newWord)
          secondSlice.forEach((w, i) => (w.partIndex = i))
        }
      }
    } else {
      const wordToMoveToNextPart = firstSlice.pop()
      if (wordToMoveToNextPart) {
        secondSlice.unshift(wordToMoveToNextPart)
      }
    }

    if (secondSlice[0] && secondSlice[0].isPunctuation) {
      const wordToMoveToPrevPart = secondSlice.shift()
      if (wordToMoveToPrevPart) {
        firstSlice.push(wordToMoveToPrevPart)
      }
    }

    /*
         For MYAN:
         This one triggers rerender of all words of part because it changes the reference to array.
         Update only changed words instead
         Need to figure out how to prevent rerender of all words of a new part
         */
    partToSplit.words = firstSlice
    partToSplit.end = partToSplit.words[partToSplit.words.length - 1].end
    newPart.words = secondSlice
    newPart.start = newPart.words[0].start
    newPart.end = newPart.words[newPart.words.length - 1].end

    if (!newPart.words?.length) {
      return null
    }

    partToSplit.words.forEach((w, index) => {
      w.partIndex = index
    })

    newPart?.words?.forEach((w, index) => {
      w.partIndex = index
    })

    parts.value.splice(partIndex + 1, 0, newPart)
    parts.value.forEach((part, index) => {
      part.index = index
    })

    return newPart
  }

  /**
   * Joins parts
   * @param firstIndex - left-hand part
   * @param lastIndex - right-hand part
   * @param firstPartDomWords - left-hand part words
   * @param lastPartDomWords - right-hand part words
   */
  function joinParts(
    firstIndex: number,
    lastIndex: number,
    firstPartDomWords: HTMLElement[],
    lastPartDomWords: HTMLElement[]
  ): Word | null {
    const firstPart = parts.value[firstIndex]
    const lastPart = parts.value[lastIndex]

    if (
      !firstPart ||
      !lastPart ||
      !firstPart.words ||
      !lastPart.words ||
      firstIndex >= lastIndex
    ) {
      return null
    }

    const firstPartWords = formatPart(firstPart, firstPartDomWords)
    let lastPartWords = formatPart(lastPart, lastPartDomWords)

    if (
      lastPartWords.length === 1 &&
      !lastPartWords[0].text.trim().length &&
      lastPartWords[0].isNew
    ) {
      lastPartWords = []
    }

    // TODO: write text mapper
    const newPart: Word = {
      id: uuidv4(),
      text: 'text',
      start: parts.value[firstIndex].start,
      end: parts.value[lastIndex].end,
      speaker: parts.value[firstIndex].speaker,
      index: -2,
      words: [...firstPartWords, ...lastPartWords],
      version: increaseVersion(parts.value[firstIndex].version)
    }

    newPart.words?.forEach((w, index) => {
      w.partIndex = index
    })

    parts.value.splice(firstIndex, 2, newPart)
    parts.value.forEach((part, index) => {
      part.index = index
    })
    return newPart
  }

  function setValue({
    _parts,
    _sentences,
    _words
  }: {
    _parts: Word[]
    _sentences: Word[]
    _words: Word[]
  }) {
    parts.value = _parts
    sentences.value = _sentences
    words.value = _words
  }

  /**
   * Join 2 closest words
   * @param partIndex - part words belong to
   * @param id - word to restore focus one
   * @param domWords - HTML elements of part words
   */
  function joinWords(
    partIndex: number,
    id: string,
    domWords: HTMLElement[]
  ): [Word, Word, number] | null {
    const part: Word = parts.value[partIndex]

    if (!part.words || !part.words.length || !id) {
      return null
    }

    // Format part to be up to date with previous changes made
    part.words = format(domWords, part.words)
    part.version = increaseVersion(part.version)

    // Since the part was formatted, focused word might change its index. Restore it by id
    const restoredWord = part.words.find(w => w.id === id)

    if (!restoredWord || restoredWord.partIndex === undefined) {
      return null
    }

    // Find closest non spare word to join with
    const closestWordToJoin: Word | undefined = getClosestNonSpareWordBefore(
      restoredWord.partIndex,
      part.words
    )

    if (!closestWordToJoin || closestWordToJoin.partIndex === undefined) {
      return null
    }

    const restoreOffset = closestWordToJoin.text.length

    if (closestWordToJoin.isPunctuation) {
      return null
    }

    // Joining
    closestWordToJoin.text = closestWordToJoin.text + restoredWord.text
    closestWordToJoin.isSpaceRequired = true
    restoredWord.text = ''
    restoredWord.isSpare = true

    // Handling the case when the next word is punctuation. We need too shift it too.
    if (
      part.words[restoredWord.partIndex + 1] &&
      part.words[restoredWord.partIndex + 1].isPunctuation
    ) {
      const punctuation = part.words[restoredWord.partIndex + 1]
      punctuation.start = closestWordToJoin.end
      punctuation.end = closestWordToJoin.end
      closestWordToJoin.isSpaceRequired = false

      part.words.splice(punctuation.partIndex || 0, 1)
      part.words.splice(closestWordToJoin.partIndex + 1, 0, punctuation)

      part.words.forEach((word, index) => {
        word.partIndex = index
      })
    }

    return [part, closestWordToJoin, restoreOffset]
  }

  /**
   * Delete words
   * @param partIndex - part words belong to
   * @param start - word index in part to start from
   * @param end - word index in part to end at
   * @param domWords - HTML elements of part words
   */
  function deleteWords(
    partIndex: number,
    start: HTMLElement,
    end: HTMLElement,
    domWords: HTMLElement[]
  ): [Word, string, Word[]] | null {
    const part: Word = parts.value[partIndex]
    if (!part || !part.words) {
      return null
    }

    part.words = format(domWords, part.words)
    part.version = increaseVersion(part.version)

    let _from = -1,
      to = -1

    // For Safari :(
    if (isWordElement(start.parentElement as HTMLElement)) {
      const startId = getWordId(start.parentElement as HTMLElement)
      const endId = getWordId(end.parentElement as HTMLElement)
      if (startId && endId) {
        const startWord = part.words.find(w => w.id === startId)
        const endWord = part.words.find(w => w.id === endId)

        if (startWord && endWord) {
          _from = startWord.partIndex !== undefined ? startWord.partIndex : -1
          to =
            endWord.partIndex || endWord.partIndex === 0
              ? endWord.partIndex
              : -1
        }
      }
    } else {
      const startId = getWordId(start as HTMLElement)
      const endId = getWordId(end as HTMLElement)

      if (startId && endId) {
        const startWord = part.words.find(w => w.id === startId)
        const endWord = part.words.find(w => w.id === endId)

        if (startWord && endWord) {
          _from = startWord.partIndex !== undefined ? startWord.partIndex : -1
          to =
            endWord.partIndex || endWord.partIndex === 0
              ? endWord.partIndex
              : -1
        }
      }
    }

    if (_from === -1 || to === -1) {
      return null
    }

    const updatedWords: Word[] = []

    for (let i = _from; i <= to; i++) {
      updatedWords.push(JSON.parse(JSON.stringify(part.words[i])))
      if (i === 0 && to === part.words.length - 1) {
        part.words[i].text = String.fromCharCode(160)
      } else {
        part.words[i].text = ''
      }
      part.words[i].isSpare = true
    }

    const focusIdx: number = part.words[_from]
      ? part.words[_from].partIndex || 0
      : 0
    let focusId = ''
    const closestNonSpareWordBefore = getClosestNonSpareWordBefore(
      focusIdx,
      part.words
    )
    //

    if (!closestNonSpareWordBefore) {
      focusId = part.words[0].id || ''
    } else {
      focusId = closestNonSpareWordBefore.id || ''
    }

    part.words = part.words.filter((w, index) =>
      index >= _from && index <= to ? !w.isPunctuation : w
    )

    if (part.words[0].isSpare) {
      part.words[0].text = String.fromCharCode(160)
      part.words[0].isSpare = false
      part.words[0].isSpaceRequired = true
    }

    return [part, focusId, updatedWords]
  }

  /**
   * Remove punctuation
   * @param partIndex - part words belong to
   * @param wordId - word to restore focus one
   * @param domWords - HTML elements of part words
   */
  function removePunctuation(
    partIndex: number,
    wordId: string,
    domWords: HTMLElement[]
  ): [Word, string | undefined] | null {
    const part = parts.value[partIndex]

    if (!part || !part.words) {
      return null
    }

    // Format to get up to date
    part.words = format(domWords, part.words)
    part.version = increaseVersion(part.version)

    if (!part.words) {
      return null
    }

    // Indices might change so let's restore the word by id
    const restoredWord = part.words.find(w => w.id === wordId)

    if (!restoredWord || restoredWord.partIndex === undefined) {
      return null
    }

    const wordIndex = restoredWord.partIndex

    // Remove punctuation
    if (part.words[wordIndex].isPunctuation) {
      part.words.splice(wordIndex, 1)
    }

    if (part.words[wordIndex - 1]) {
      part.words[wordIndex - 1].isSpaceRequired = true
    }

    part.edited = false

    parts.value.forEach((part, index) => {
      part.index = index
    })
    const wordToRestoreFocusOn = getClosestNonSpareWordBefore(
      wordIndex,
      part.words
    )
    return [part, wordToRestoreFocusOn?.id]
  }

  /**
   * Delete part
   * @param index - part index
   * @param htmlWords - part words html elements
   */
  function deletePart(index: number, htmlWords: HTMLElement[]) {
    const part = parts.value[index]
    const prevPart = parts.value[index - 1]
    if (!part.words || !prevPart) {
      return null
    }

    part.words = format(htmlWords, part.words)
    part.version = increaseVersion(part.version)

    if (!part.words) {
      return null
    }

    if (part.isNew) {
      prevPart.end = part.end
    }

    if (!prevPart.words) {
      return null
    }

    prevPart.words = [...prevPart.words, ...part.words]
    prevPart.words.forEach((w, i) => {
      w.partIndex = i
    })
    prevPart.version = increaseVersion(prevPart.version)
    prevPart.edited = false

    parts.value.splice(part.index, 1)

    return updatePart(prevPart, prevPart.index)
  }

  /**
   * Reverts last change
   * @param item - id - part id to undo, words - part words to revert
   */
  function undo(item: { id: string; words: Word[] } | undefined) {
    if (!item) {
      return
    }

    const part = parts.value.find(p => p.id === item.id)

    if (!part || !part.words) {
      return
    }

    part.words = [...item.words]
    part.version = increaseVersion(part.version)
  }

  return {
    parts,
    sentences,
    words,
    value,
    version,
    deletePart,
    initTranscript,
    updTranscript,
    format,
    joinWords,
    removePunctuation,
    joinParts,
    deleteWords,
    insertEmptyPart,
    formatAll,
    formatEdited,
    splitPart,
    extractWords,
    saveWords,
    formatPart,
    insertEmptyPartBefore,
    setValue,
    undo
  }
}
